import React from "react"
import { EOrderCurrentStatus } from "../../../Enum/EOrderCurrentStatus"
import { useMinutesTimer } from "./KitchenOrderCard"
import { OrderDrink } from "../../../Objects/Order/OrderDrink"
import { OrderMeal } from "../../../Objects/Order/OrderMeal"
import { CanView } from "../../../components/CanView/CanView"
import { Permissions } from "../../../Enum/PermissionsType"
import { useTranslation } from "react-i18next"

interface MealItemProps {
  meal: OrderDrink | OrderMeal
  orderId: number
  isDelivery: boolean
  canShowActionButtons: boolean
  handleStatusItem: (mealId: number, isNextStep: boolean) => void
}
export const MealItem: React.FC<MealItemProps> = ({ meal, isDelivery, canShowActionButtons, handleStatusItem }) => {
  const { t } = useTranslation("rodizio")

  const mealTimer = useMinutesTimer(meal.date)

  const getTimerColor = () => {
    if (mealTimer > 20) {
      return "text-red-600 animate-pulse font-bold"
    }

    if (mealTimer > 10) {
      return "text-orange-500 font-semibold"
    }

    return "text-slate-500"
  }

  return (
    <div className="rounded-xl border border-slate-200 p-4 mb-3 bg-slate-50 hover:bg-white transition">
      <div className="flex justify-between items-center mb-3">
        <span className="text-xs font-bold uppercase bg-white ring-2 ring-red-300 px-3 py-1 rounded-full">
          {meal.currentStatus.toOrderCurrentStatusString()}
        </span>

        <span className={`text-sm ${getTimerColor()}`}>{mealTimer} min</span>
      </div>

      <div className="font-bold text-slate-800">{meal.getName()}</div>

      {meal.rodizioParticipantName && (
        <div className="mt-2 inline-flex rounded-full bg-orange-100 px-3 py-1 text-xs font-black text-orange-800">
          {t("kitchen.participant", { name: meal.rodizioParticipantName })}
        </div>
      )}

      {meal.addtionals.length > 0 && (
        <ul className="text-sm text-slate-500 mt-2 space-y-1 list-disc list-inside">
          {meal.addtionals.map((add: any, idx: number) => (
            <li key={idx}>
              + {add.addtionalEntity?.name} (x{add.amount})
            </li>
          ))}
        </ul>
      )}

      {meal.selections?.length > 0 && (
        <ul className="text-sm text-indigo-600 mt-2 space-y-1 list-disc list-inside">
          {meal.selections.map((selection: any, idx: number) => (
            <li key={`${selection.selectionEntityId ?? "selection"}-${idx}`}>
              {selection.selectionEntity?.name ?? "Seleção"}: {selection.selectionOptionEntity?.name ?? "Opção"}
            </li>
          ))}
        </ul>
      )}

      {meal.description && <p className="text-xs italic text-red-500 mt-2">Obs: {meal.description}</p>}

      <CanView permission={Permissions.CanEditeKitchen}>
        {!meal.isClosed() && (
          <div className="flex gap-2 mt-4">
            {canShowActionButtons && meal.currentStatus !== EOrderCurrentStatus.Pending && meal.needKitchen() && (
              <button
                onClick={() => handleStatusItem(meal.id, false)}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white
                         py-2 rounded-lg font-semibold transition"
                data-interaction-description-key="interactionDescriptions:pages.kitchenPage.components.mealItem.button.handleStatusItemMealIdFalse"
              >
                Voltar
              </button>
            )}

            {canShowActionButtons && meal.currentStatus !== EOrderCurrentStatus.Finished && (
              <button
                onClick={() => handleStatusItem(meal.id, true)}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white
                         py-2 rounded-lg font-semibold transition"
                data-interaction-description-key="interactionDescriptions:pages.kitchenPage.components.mealItem.button.handleStatusItemMealIdTrue"
              >
                {meal.currentStatus === EOrderCurrentStatus.Pending ? "Fazer" : "Pronto"}
              </button>
            )}

            {canShowActionButtons && !isDelivery && meal.currentStatus === EOrderCurrentStatus.Finished && (
              <button
                onClick={() => handleStatusItem(meal.id, true)}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white
                         py-2 rounded-lg font-semibold transition"
                data-interaction-description-key="interactionDescriptions:pages.kitchenPage.components.mealItem.button.handleStatusItemMealIdTrue2"
              >
                {"Entregue"}
              </button>
            )}
          </div>
        )}
      </CanView>
    </div>
  )
}
